import { useState } from "react";

import { ALL_FILTER, FilterSelect } from "@/components/filter-select";
import { PageHeader } from "@/components/page-header";
import { QueryErrorCard } from "@/components/query-error-card";
import { TableSkeleton } from "@/components/table-skeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminAccounts, useAdminMcpAnalytics, useAdminMcpToolNames } from "@/lib/api";
import { cn } from "@/lib/utils";

import { McpDashboard } from "./mcp-dashboard";

const DAY_OPTIONS = [7, 30, 90];

export function AdminMcpInsightsPage() {
  const [days, setDays] = useState(30);
  const [accountFilter, setAccountFilter] = useState(ALL_FILTER);
  const accountId = accountFilter === ALL_FILTER ? undefined : accountFilter;

  const analyticsQuery = useAdminMcpAnalytics(days, accountId);
  const accountsQuery = useAdminAccounts();
  const toolsQuery = useAdminMcpToolNames();

  const accounts = accountsQuery.data ?? [];

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6">
      <PageHeader
        eyebrow="MCP usage insights"
        title="How customers are using MCP"
        description="Tool calls, denied requests, orders placed, and a full audit trail across every customer connecting their agents to the platform."
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <FilterSelect
              value={accountFilter}
              onValueChange={setAccountFilter}
              allLabel="All customers"
              options={accounts.map((account) => ({
                value: account.account_id,
                label: account.account_name,
              }))}
              className="w-48"
            />
            <div
              role="group"
              aria-label="Time range"
              className="border-border bg-card inline-flex overflow-hidden rounded-md border"
            >
              {DAY_OPTIONS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  aria-pressed={days === d}
                  onClick={() => setDays(d)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium transition-colors",
                    i < DAY_OPTIONS.length - 1 && "border-border border-r",
                    days === d
                      ? "bg-primary text-primary-foreground"
                      : "text-muted hover:bg-warm-gray-02 hover:text-foreground",
                  )}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
        }
      />

      {analyticsQuery.isLoading ? (
        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-[4.5rem] rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-56 w-full rounded-lg" />
          <TableSkeleton columns={6} />
        </div>
      ) : analyticsQuery.isError || !analyticsQuery.data ? (
        <QueryErrorCard
          message="Could not load MCP insights."
          onRetry={() => void analyticsQuery.refetch()}
          backTo="/home"
        />
      ) : (
        <McpDashboard
          analytics={analyticsQuery.data}
          accounts={accounts}
          tools={toolsQuery.data ?? []}
          days={days}
        />
      )}
    </div>
  );
}
